'use client';

import { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Button } from "react-bootstrap";
import Select from "react-select";
import {
  createDoctorAction,
  updateDoctorAction,
} from "../../../store/actions/DoctorsAction";
import { showErrorAlert } from "../../../services/ErrorHandlingService";
import Loader from "../Common/Loader";
import { getBranchesAction } from "../../../store/actions/BranchAction";

const defaultWorkingHours = {
  Monday: [{ from: "08:00", to: "15:00", branch: "", no_of_appointments: 10 }],
  Tuesday: [{ from: "08:00", to: "17:30", branch: "", no_of_appointments: 10 }],
  Wednesday: [{ from: "08:00", to: "17:30", branch: "", no_of_appointments: 10 }],
  Thursday: [{ from: "08:00", to: "17:30", branch: "", no_of_appointments: 10 }],
  Friday: [{ from: "08:00", to: "17:30", branch: "", no_of_appointments: 10 }],
  Saturday: [{ from: "09:00", to: "13:00", branch: "", no_of_appointments: 10 }],
  Sunday: [{ from: "09:00", to: "13:00", branch: "", no_of_appointments: 10 }],
};

// Helper: 24-hour to 12-hour format
const formatTimeTo12Hour = (time) => {
  if (!time) return null;
  let [hour, minute] = time.split(":");
  hour = parseInt(hour, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  hour = hour % 12 || 12;
  return `${hour}:${minute}${ampm}`;
};

// Helper: 12-hour to 24-hour format
const convert12To24 = (time12h) => {
  if (!time12h) return "";
  const [time, modifier] = time12h.split(/(am|pm)/);
  let [hours, minutes] = time.split(":");
  hours = parseInt(hours, 10);
  if (modifier === "pm" && hours !== 12) hours += 12;
  if (modifier === "am" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

// Convert UI state to backend format
const convertWorkingHoursToOpeningHrs = (workingHours, unavailableDays) => {
  return Object.keys(workingHours).map((day) => {
    const isUnavailable = unavailableDays[day];

    return {
      day,
      status: isUnavailable ? "Closed" : "Active",
      time: isUnavailable
        ? []
        : workingHours[day].map((slot) => ({
          open_time: formatTimeTo12Hour(slot.from),
          close_time: formatTimeTo12Hour(slot.to),
          branch: slot.branch || "",
          no_of_appointments: Number(slot.no_of_appointments || 0),
        })),
    };
  });
};

// Parse backend format to UI state
const parseOpeningHrsToState = (openingHrs) => {
  const workingHours = JSON.parse(JSON.stringify(defaultWorkingHours));
  const unavailableDays = {};

  Object.keys(defaultWorkingHours).forEach((day) => {
    unavailableDays[day] = false;
  });

  if (!Array.isArray(openingHrs) || openingHrs.length === 0) {
    return { workingHours, unavailableDays };
  }

  // Group opening hours by day
  const groupedByDay = {};
  openingHrs.forEach((slot) => {
    const day = slot.day;
    if (!groupedByDay[day]) {
      groupedByDay[day] = [];
    }
    groupedByDay[day].push(slot);
  });

  // Process each day's slots
  Object.keys(groupedByDay).forEach((day) => {
    const daySlots = groupedByDay[day];

    // Check if all slots for this day have empty status (meaning open/active)
    const isAllClosed = daySlots.every((slot) => slot.status === "Closed" || (slot.status === "" && !slot.open_time));

    if (isAllClosed && daySlots[0].status === "Closed") {
      unavailableDays[day] = true;
      return;
    }

    // Map slots to working hours format (handle both formats)
    workingHours[day] = daySlots
      .filter((slot) => slot.open_time && slot.close_time) // Only include slots with times
      .map((slot) => ({
        from: slot.open_time ? convert12To24(slot.open_time) : "",
        to: slot.close_time ? convert12To24(slot.close_time) : "",
        branch: slot.branch || "",
        no_of_appointments: Number(slot.no_of_appointments || 0),
      }));


    // If no valid slots, mark as unavailable
    if (workingHours[day].length === 0) {
      unavailableDays[day] = true;
    }
  });
  Object.keys(defaultWorkingHours).forEach((day) => {
    if (!groupedByDay[day]) {
      unavailableDays[day] = true;
      workingHours[day] = [];
    }
  });
  return { workingHours, unavailableDays };
};

const AddDoctor = ({ doctorDataFromBackend }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  // Get doctor from navigation state (edit mode)
  const doctorFromState = location.state?.doctor || null;
  const isEditMode = !!doctorFromState;

  const [doctor, setDoctor] = useState({
    doctor_username: "",
    doctor_password: "",
    doctor_code: "",
    doctor_name: "",
    specialization: "",
    contact_number: "",
    email: "",
    address: "",
    consultation_mode: "",
    doctor_status: "",
    doctor_type: "",
    doctor_id: "",
    first_time_visitor: "",
    followup_patient: "",
  });

  const [selectedBranches, setSelectedBranches] = useState([]);

  const branchList = useSelector((state) => state.branches.branches);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [errors, setErrors] = useState({});
  const errorFieldsRef = useRef({});

  const [workingHoursEnabled, setWorkingHoursEnabled] = useState(true);
  const [unavailableDays, setUnavailableDays] = useState(() => {
    const init = {};
    Object.keys(defaultWorkingHours).forEach((day) => {
      init[day] = false;
    });
    return init;
  });

  const [workingHours, setWorkingHours] = useState(defaultWorkingHours);

  useEffect(() => {
    dispatch(getBranchesAction());
  }, [dispatch]);

  const branchOptions = branchList?.map(branch => ({
    value: branch.branch_id,
    label: branch.branch_name,
  }));

  // EDIT FLOW: Initialize state from backend data or navigation state
  useEffect(() => {
    let doc = doctorFromState;
    if (doc) {
      setFetching(true);
      setErrors("");

      // Handle both opening_hrs and OpeningHrs (API returns OpeningHrs)
      const openingHrsData = doc.opening_hrs || doc.OpeningHrs || [];
      console.log("[v0] Opening hours data from API:", openingHrsData);
      const { workingHours: wh, unavailableDays: ud } = parseOpeningHrsToState(
        openingHrsData
      );
      console.log("[v0] Parsed working hours:", wh);
      console.log("[v0] Parsed unavailable days:", ud);
      setWorkingHours(wh);
      setUnavailableDays(ud);

      setDoctor({
        doctor_username: doc.doctor_username || "",
        doctor_password: "",
        doctor_code: doc.doctor_code || "",
        doctor_name: doc.doctor_name || "",
        specialization: doc.specialization || "",
        contact_number: doc.contact_number || "",
        email: doc.email_address || doc.email || "",
        address: doc.address || "",
        consultation_mode: doc.consultation_mode || "",
        doctor_status: doc.doctor_status || "",
        doctor_id: doc.doctor_id || "",
        doctor_type: doc.doctor_type || "",
        first_time_visitor: doc.consulting_duration_first_time || "",
        followup_patient: doc.followup_patient || "",
      });
      setFetching(false);
    }
  }, [doctorDataFromBackend, doctorFromState]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setDoctor((prevDoctor) => ({
      ...prevDoctor,
      [name]: value,
    }));

    // Real-time error clearing
    if (errors[name]) {
      setErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        delete updatedErrors[name];
        return updatedErrors;
      });
    }
  };

  // Validate time slots for a specific day
  const validateTimeSlots = (daySlots) => {
    for (let i = 0; i < daySlots.length; i++) {
      const slot = daySlots[i];
      if (slot.from && slot.to && slot.from >= slot.to) {
        return false;
      }
      
      // Check for overlaps with next slot
      if (i < daySlots.length - 1) {
        const nextSlot = daySlots[i + 1];
        if (slot.to && nextSlot.from && slot.to > nextSlot.from) {
          return false;
        }
      }
    }
    return true;
  };

  const addTimeSlot = (day) => {
    setWorkingHours((prev) => {
      const currentSlots = [...prev[day]];
      const lastSlot = currentSlots[currentSlots.length - 1];
      
      // Calculate new start time based on last slot's end time
      let newStartTime = "09:00"; // Default fallback
      if (lastSlot && lastSlot.to) {
        newStartTime = lastSlot.to;
      }
      
      // Calculate default end time (1 hour after start)
      let newEndTime = "17:00";
      if (newStartTime) {
        const [hours, minutes] = newStartTime.split(":");
        let endHour = parseInt(hours) + 1;
        newEndTime = `${endHour.toString().padStart(2, "0")}:${minutes}`;
      }
      
      const newSlot = {
        from: newStartTime,
        to: newEndTime,
        branch: "",
        no_of_appointments: 10
      };
      
      const updatedSlots = [...currentSlots, newSlot];
      
      // Validate the new slots
      if (!validateTimeSlots(updatedSlots)) {
        setTimeout(() => {
          showErrorAlert("Time slots cannot overlap! Please adjust the times.");
        }, 100);
      }
      
      return {
        ...prev,
        [day]: updatedSlots,
      };
    });
  };

  const removeTimeSlot = (day, index) => {
    const updatedSlots = [...workingHours[day]];
    updatedSlots.splice(index, 1);
    setWorkingHours((prev) => ({
      ...prev,
      [day]: updatedSlots,
    }));
  };

  const handleTimeChange = (day, index, field, value) => {
    setWorkingHours((prev) => {
      const updated = [...prev[day]];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      
      // If changing the 'from' time of a slot that's not the first, check if it matches previous slot's 'to'
      if (field === 'from' && index > 0) {
        const previousSlot = updated[index - 1];
        if (previousSlot.to && value !== previousSlot.to) {
          setTimeout(() => {
            showErrorAlert(`Start time should match previous slot's end time (${previousSlot.to}) for sequential slots.`);
          }, 100);
        }
      }
      
      // If changing 'to' time, check if it's greater than 'from'
      if (field === 'to' && updated[index].from && value <= updated[index].from) {
        setTimeout(() => {
          showErrorAlert("End time must be greater than start time.");
        }, 100);
        return prev;
      }
      
      // Validate all slots after update
      if (!validateTimeSlots(updated)) {
        setTimeout(() => {
          showErrorAlert("Time slots cannot overlap! Please adjust the times.");
        }, 100);
      }
      
      return { ...prev, [day]: updated };
    });
  };

  const handleBranchChangeForSlot = (day, index, selectedOption) => {
    setWorkingHours((prev) => {
      const updated = [...prev[day]];
      updated[index] = {
        ...updated[index],
        branch: selectedOption?.label || "",
      };
      return { ...prev, [day]: updated };
    });
  };

  const handleAppointmentsChange = (day, index, value) => {
    setWorkingHours((prev) => {
      const updated = [...prev[day]];
      updated[index] = {
        ...updated[index],
        no_of_appointments: Number(value) || 0,
      };
      return { ...prev, [day]: updated };
    });
  };

  const handleBranchChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setSelectedBranches(selectedOption);
    setDoctor(prev => ({
      ...prev,
      branches: value,
    }));
  };

  const copyToAll = (day) => {
    const slots = workingHours[day];
    setWorkingHours((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((d) => {
        if (d !== day && !unavailableDays[d]) {
          updated[d] = slots.map((slot) => ({ ...slot }));
        }
      });
      return updated;
    });
  };

  const toggleDayAvailability = (day, isUnavailable) => {
    setUnavailableDays((prev) => ({
      ...prev,
      [day]: isUnavailable,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!isEditMode && !doctor.doctor_name.trim()) {
      newErrors.doctor_name = "Enter doctor name";
    }

    if (!doctor.doctor_code.trim()) {
      newErrors.doctor_code = "Doctor Code is required";
    }

    if (!doctor.contact_number.trim()) {
      newErrors.contact_number = "Contact Number is required";
    } else if (!/^\d{10}$/.test(doctor.contact_number)) {
      newErrors.contact_number = "Contact Number must be 10 digits";
    }

    if (!doctor.email.trim()) {
      newErrors.email = "Email Address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(doctor.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (
      doctor.first_time_visitor === undefined ||
      doctor.first_time_visitor === "" ||
      isNaN(doctor.first_time_visitor)
    ) {
      newErrors.first_time_visitor = "Enter first time visitor count";
    } else if (parseInt(doctor.first_time_visitor, 10) < 0) {
      newErrors.first_time_visitor = "Count cannot be negative";
    }

    // ✅ Follow-up Patient validation
    if (
      doctor.followup_patient === undefined ||
      doctor.followup_patient === "" ||
      isNaN(doctor.followup_patient)
    ) {
      newErrors.followup_patient = "Enter follow-up patient count";
    } else if (parseInt(doctor.followup_patient, 10) < 0) {
      newErrors.followup_patient = "Count cannot be negative";
    }

    if (!doctor.specialization) {
      newErrors.specialization = "Enter specialization";
    }

    if (!doctor.consultation_mode) {
      newErrors.consultation_mode = "Select consultation mode";
    }

    if (!doctor.address) {
      newErrors.address = "Enter Address";
    }

    if (!doctor.doctor_status) {
      newErrors.doctor_status = "Status is required";
    }

    if (!doctor.doctor_username) {
      newErrors.doctor_username = "Enter Username";
    }

    if (!doctor.doctor_type) {
      newErrors.doctor_type = "Doctor type is required";
    }

    if (!isEditMode && !doctor.doctor_password) {
      newErrors.doctor_password = "Enter Password";
    }

    // Check at least one day is available
    const allDaysUnavailable = Object.values(unavailableDays).every((val) => val === true);
    if (allDaysUnavailable) {
      newErrors.workingHours = "At least one working day must be available.";
    }

    // Validate time slots for all days
    Object.keys(workingHours).forEach(day => {
      if (!unavailableDays[day] && workingHours[day].length > 0) {
        if (!validateTimeSlots(workingHours[day])) {
          newErrors[`timeSlot_${day}`] = `Time slots for ${day} have overlaps or invalid times.`;
        }
      }
    });

    setErrors(newErrors);

    // Scroll to first error field
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = errorFieldsRef.current[firstErrorField];
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus();
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors("");

    if (!validate()) {
      setLoading(false);
      return;
    }

    const openingHrs = convertWorkingHoursToOpeningHrs(
      workingHours,
      unavailableDays
    );

    console.log("[v0] Opening hours being sent:", openingHrs);

    const doctorData = {
      doctor_code: doctor.doctor_code,
      doctor_name: doctor.doctor_name,
      specialization: doctor.specialization,
      contact_number: doctor.contact_number,
      email_address: doctor.email,
      address: doctor.address,
      consultation_mode: doctor.consultation_mode,
      consulting_duration_first_time: Number(doctor.first_time_visitor) || 0,
      followup_patient: Number(doctor.followup_patient) || 0, // ✅ Fixed: ensure followup_patient is sent
      doctor_status: doctor.doctor_status,
      doctor_username: doctor.doctor_username,
      doctor_password: isEditMode ? doctor.doctor_password || null : doctor.doctor_password,
      doctor_type: doctor.doctor_type,
      opening_hrs: openingHrs,
    };

    console.log("[v0] Complete doctor payload being sent:", JSON.stringify(doctorData, null, 2));

    if (isEditMode) {
      dispatch(updateDoctorAction(doctorData, doctor.doctor_id))
        .then(() => {
          navigate("/doctors");
        })
        .catch((err) => {
          console.error("Error updating doctor:", err);
          setErrors("Failed to update doctor");
          showErrorAlert("Failed to update doctor");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      dispatch(createDoctorAction(doctorData))
        .then(() => {
          navigate("/doctors");
        })
        .catch((err) => {
          console.error("Error adding doctor:", err);
          setErrors("Failed to add doctor");
          showErrorAlert("Failed to add doctor");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      fontSize: '14px',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
    }),
    input: (base) => ({
      ...base,
      margin: '0px',
      fontSize: '14px',
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '14px',
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: '14px',
    }),
  };

  if (fetching) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="d-flex justify-content-end p-3">
          <Link to="/doctors">
            <Button variant="primary">Back</Button>
          </Link>
        </div>
        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Doctor Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="doctor_code"
                    placeholder="Enter Doctor Code"
                    value={doctor.doctor_code || ""}
                    onChange={handleChange}
                    ref={(el) => (errorFieldsRef.current.doctor_code = el)}
                  />
                  {errors.doctor_code && (
                    <div className="text-danger d-block mb-2">{errors.doctor_code}</div>
                  )}
                </div>
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Name<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="doctor_name"
                    placeholder="Enter Name"
                    value={doctor.doctor_name || ""}
                    onChange={handleChange}
                    ref={(el) => (errorFieldsRef.current.doctor_name = el)}
                  />
                  {errors.doctor_name && (
                    <div className="text-danger d-block mb-2">{errors.doctor_name}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="email"
                    placeholder="Enter Email Address"
                    value={doctor.email || ""}
                    onChange={handleChange}
                    ref={(el) => (errorFieldsRef.current.email = el)}
                  />
                  {errors.email && (
                    <div className="text-danger d-block mb-2">{errors.email}</div>
                  )}
                </div>
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Contact Number<span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="contact_number"
                    placeholder="Enter Contact Number"
                    value={doctor.contact_number || ""}
                    onChange={handleChange}
                    maxLength={10}
                    ref={(el) => (errorFieldsRef.current.contact_number = el)}
                  />
                  {errors.contact_number && (
                    <div className="text-danger d-block mb-2">{errors.contact_number}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Doctor Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="doctor_type"
                    options={[
                      { value: "", label: "Select Doctor Type" },
                      { value: "Main", label: "Main" },
                      { value: "Assistant", label: "Assistant" },
                      { value: "Franchise", label: "Franchise" },
                    ]}
                    value={
                      doctor.doctor_type
                        ? { value: doctor.doctor_type, label: doctor.doctor_type }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleChange({
                        target: {
                          name: "doctor_type",
                          value: selectedOption.value,
                        },
                      })
                    }
                    placeholder="Select Doctor Type"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        lineHeight: "50px",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                    }}
                  />
                  {errors.doctor_type && (
                    <div className="text-danger d-block mb-2">{errors.doctor_type}</div>
                  )}
                </div>
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Specialization<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="specialization"
                    placeholder="Enter Specialization"
                    value={doctor.specialization || ""}
                    onChange={handleChange}
                    ref={(el) => (errorFieldsRef.current.specialization = el)}
                  />
                  {errors.specialization && (
                    <div className="text-danger d-block mb-2">{errors.specialization}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Consultation Mode<span className="text-danger">*</span>
                  </label>
                  <Select
                    name="consultation_mode"
                    options={[
                      { value: "", label: "Select Mode" },
                      { value: "In-person", label: "In-person" },
                      { value: "Online", label: "Online" },
                      { value: "Both", label: "Both" },
                    ]}
                    value={
                      doctor.consultation_mode
                        ? {
                          value: doctor.consultation_mode,
                          label: doctor.consultation_mode || "",
                        }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleChange({
                        target: {
                          name: "consultation_mode",
                          value: selectedOption.value,
                        },
                      })
                    }
                    placeholder="Select Consultation Mode"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        lineHeight: "50px",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: "none",
                      }),
                    }}
                  />
                  {errors.consultation_mode && (
                    <div className="text-danger d-block mb-2">{errors.consultation_mode}</div>
                  )}
                </div>
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Consulting Duration for First time Visitor (Minutes) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="first_time_visitor"
                    placeholder="Enter Consulting Duration for First Time Visitor"
                    value={doctor.first_time_visitor || ""}
                    onChange={handleChange}
                    min={0}
                    step={1}
                    ref={(el) => (errorFieldsRef.current.first_time_visitor = el)}
                  />
                  {errors.first_time_visitor && (
                    <div className="text-danger d-block mb-2">
                      {errors.first_time_visitor}
                    </div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Follow-Up Patient (Minutes) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="followup_patient"
                    placeholder="Enter Follow-Up Patient duration in minutes"
                    value={doctor.followup_patient || ""}
                    onChange={handleChange}
                    min={0}
                    step={1}
                    ref={(el) => (errorFieldsRef.current.followup_patient = el)}
                  />
                  {errors.followup_patient && (
                    <div className="text-danger d-block mb-2">
                      {errors.followup_patient}
                    </div>
                  )}
                </div>
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Address <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    placeholder="Enter Address"
                    value={doctor.address || ""}
                    onChange={handleChange}
                    ref={(el) => (errorFieldsRef.current.address = el)}
                  />
                  {errors.address && (
                    <div className="text-danger d-block mb-2">{errors.address}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label" htmlFor="status">
                    Status<span className="text-danger">*</span>
                  </label>
                  <Select
                    name="doctor_status"
                    options={[
                      { value: "", label: "Select Status" },
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                    value={
                      doctor.doctor_status
                        ? {
                          value: doctor.doctor_status,
                          label: doctor.doctor_status,
                        }
                        : null
                    }
                    onChange={(selectedOption) =>
                      handleChange({
                        target: {
                          name: "doctor_status",
                          value: selectedOption.value,
                        },
                      })
                    }
                    placeholder="Select Status"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        lineHeight: "50px",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: "none",
                      }),
                    }}
                  />
                  {errors.doctor_status && (
                    <div className="text-danger d-block mb-2">{errors.doctor_status}</div>
                  )}
                </div>
              </div>

              <hr className="my-4" />

              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Doctor Username <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="doctor_username"
                    placeholder="Enter Username"
                    value={doctor.doctor_username || ""}
                    onChange={handleChange}
                    ref={(el) => (errorFieldsRef.current.doctor_username = el)}
                  />
                  {errors.doctor_username && (
                    <div className="text-danger d-block mb-2">{errors.doctor_username}</div>
                  )}
                </div>

                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Password <span className="text-danger">*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="form-control"
                      name="doctor_password"
                      placeholder="Enter Password"
                      value={doctor.doctor_password || ""}
                      onChange={handleChange}
                      style={{ paddingRight: "45px" }}
                      ref={(el) => (errorFieldsRef.current.doctor_password = el)}
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        top: "50%",
                        right: "15px",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        zIndex: 2,
                        color: "#888",
                      }}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  </div>
                  {errors.doctor_password && (
                    <div className="text-danger d-block mt-1">{errors.doctor_password}</div>
                  )}
                </div>
              </div>

              <hr className="my-4" />

              <div className="row">
                <div className="col-xl-12">
                  <div className="card shadow-none">
                    <div className="card-body">
                      <div className="form-check mb-4">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="enable-hours"
                          checked={workingHoursEnabled}
                          onChange={(e) =>
                            setWorkingHoursEnabled(e.target.checked)
                          }
                        />
                        <label
                          htmlFor="enable-hours"
                          className="form-check-label ms-2"
                        >
                          Enable working hours
                        </label>
                      </div>
                      {errors.workingHours && (
                        <div
                          className="text-danger mb-3"
                          ref={(el) => (errorFieldsRef.current.workingHours = el)}
                        >
                          {errors.workingHours}
                        </div>
                      )}

                      {workingHoursEnabled &&
                        Object.keys(workingHours).map((day) => (
                          <div key={day} className="mb-4 border-bottom pb-3">
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "10px",
                                marginBottom: "16px",
                              }}
                            >
                              <h5
                                style={{
                                  fontWeight: "600",
                                  margin: 0,
                                  fontSize: "16px",
                                  flex: "1 1 auto",
                                }}
                              >
                                {day}
                              </h5>

                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "12px",
                                  flex: "0 0 auto",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    id={`unavailable-${day}`}
                                    checked={unavailableDays[day]}
                                    onChange={(e) => toggleDayAvailability(day, e.target.checked)}
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      cursor: "pointer",
                                    }}
                                  />
                                  <label
                                    htmlFor={`unavailable-${day}`}
                                    style={{
                                      fontSize: "14px",
                                      color: "#333",
                                      cursor: "pointer",
                                      userSelect: "none",
                                    }}
                                  >
                                    Not Available
                                  </label>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => copyToAll(day)}
                                  disabled={unavailableDays[day]}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: unavailableDays[day] ? "#bbb" : "#0d6efd",
                                    textDecoration: "underline",
                                    fontSize: "14px",
                                    cursor: unavailableDays[day] ? "not-allowed" : "pointer",
                                    padding: 0,
                                  }}
                                >
                                  Copy to all
                                </button>
                              </div>
                            </div>

                            {errors[`timeSlot_${day}`] && (
                              <div className="text-danger mb-2" style={{ fontSize: "12px" }}>
                                {errors[`timeSlot_${day}`]}
                              </div>
                            )}

                            {unavailableDays[day] ? (
                              <div className="text-muted fst-italic">
                                Not available on this day
                              </div>
                            ) : (
                              <>
                                {workingHours[day].map((slot, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      gap: "10px",
                                      marginBottom: "12px",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "relative",
                                        flex: "0 0 200px",
                                      }}
                                    >
                                      <input
                                        type="time"
                                        className="form-control"
                                        value={slot.from}
                                        onChange={(e) =>
                                          handleTimeChange(day, index, "from", e.target.value)
                                        }
                                        required
                                        readOnly={index > 0}
                                        style={{
                                          width: "100%",
                                          paddingRight: "36px",
                                          height: "38px",
                                          fontSize: "14px",
                                          backgroundColor: index > 0 ? "#f5f5f5" : "white",
                                          cursor: index > 0 ? "not-allowed" : "pointer",
                                        }}
                                        title={index > 0 ? "Start time is auto-set from previous slot's end time" : ""}
                                      />
                                      <i
                                        className="fa-regular fa-clock"
                                        style={{
                                          position: "absolute",
                                          right: "10px",
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          fontSize: "16px",
                                          color: "#aaa",
                                          pointerEvents: "none",
                                        }}
                                      />
                                    </div>

                                    <span style={{ flex: "0 0 auto", fontSize: "14px", color: "#555" }}>to</span>

                                    <div
                                      style={{
                                        position: "relative",
                                        flex: "0 0 180px",
                                      }}
                                    >
                                      <input
                                        type="time"
                                        className="form-control"
                                        value={slot.to}
                                        onChange={(e) =>
                                          handleTimeChange(day, index, "to", e.target.value)
                                        }
                                        required
                                        style={{
                                          width: "100%",
                                          paddingRight: "36px",
                                          height: "38px",
                                          fontSize: "14px",
                                        }}
                                      />
                                      <i
                                        className="fa-regular fa-clock"
                                        style={{
                                          position: "absolute",
                                          right: "10px",
                                          top: "50%",
                                          transform: "translateY(-50%)",
                                          fontSize: "16px",
                                          color: "#aaa",
                                          pointerEvents: "none",
                                        }}
                                      />
                                    </div>

                                    <div style={{ flex: "0 0 200px" }}>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Total appointments"
                                        value={slot.no_of_appointments || ""}
                                        onChange={(e) =>
                                          handleAppointmentsChange(day, index, e.target.value)
                                        }
                                        min="0"
                                        style={{
                                          width: "100%",
                                          height: "38px",
                                          border: "1px solid #ced4da",
                                          borderRadius: "4px",
                                          fontSize: "14px",
                                          paddingLeft: "8px",
                                        }}
                                      />
                                    </div>

                                    <div style={{ flex: "0 0 220px", position: "relative", zIndex: 1 }}>
                                      <Select
                                        options={branchOptions}
                                        value={
                                          slot.branch
                                            ? branchOptions?.find((opt) => opt.label === slot.branch)
                                            : null
                                        }
                                        onChange={(selectedOption) =>
                                          handleBranchChangeForSlot(day, index, selectedOption)
                                        }
                                        placeholder="Select Branch"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        styles={{
                                          ...customStyles,
                                          menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                          }),
                                          menu: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            marginTop: 2,
                                          }),
                                        }}
                                      />
                                    </div>

                                    {workingHours[day].length > 1 && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => removeTimeSlot(day, index)}
                                        style={{
                                          width: "38px",
                                          height: "38px",
                                          minWidth: "38px",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          padding: 0,
                                          flex: "0 0 auto",
                                        }}
                                      >
                                        <i className="fa fa-trash" />
                                      </Button>
                                    )}
                                  </div>
                                ))}

                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => addTimeSlot(day)}
                                >
                                  <i className="fa fa-plus" /> Add Time Slot
                                </Button>
                              </>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={loading || fetching}
              >
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">Submitting...</span>
                  </>
                ) : (
                  <>{isEditMode ? "Update Doctor" : "Add Doctor"}</>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
};

AddDoctor.propTypes = {
  doctorDataFromBackend: PropTypes.shape({
    opening_hrs: PropTypes.array,
  }),
};

export default AddDoctor;